import { useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { setCurrentCaseId } from '../../redux/slices/caseSlice';
import { setCurrentChatId } from '../../redux/slices/chatSlice';
import { setNewCurrentCaseThunk } from '../../redux/thunks/CaseThunks';
import { useOrgSlug, buildOrgHomeUrl, buildCaseUrl, buildChatUrl, getOrgSlug } from '../../utils/urlHelpers';

/**
 * Component to synchronize URL parameters with Redux state
 * Handles bidirectional sync:
 * 1. URL -> Redux (on initial load or back/forward navigation)
 * 2. Redux -> URL (when user selects case/chat in UI)
 * 
 * Uses refs to track the source of truth and prevent race conditions.
 */
const UrlSync = () => {
    const { orgSlug: urlOrgSlug, caseId, chatId } = useParams<{ orgSlug: string; caseId: string; chatId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const currentOrgSlug = useOrgSlug();
    const currentOrganization = useAppSelector(state => state.organization.currentOrganization);

    const currentCaseId = useAppSelector(state => state.CaseReducer.currentCaseId);
    const currentChatId = useAppSelector(state => state.ChatReducer.currentChatId);

    // Refs to track current Redux state without triggering effects
    const currentCaseIdRef = useRef(currentCaseId);
    const currentChatIdRef = useRef(currentChatId);

    // Ref to track if we are currently syncing from URL to Redux
    // This prevents the Redux -> URL effect from firing immediately after a URL change
    const isSyncingFromUrl = useRef(false);

    // Update refs when Redux state changes
    useEffect(() => {
        currentCaseIdRef.current = currentCaseId;
        currentChatIdRef.current = currentChatId;
    }, [currentCaseId, currentChatId]);

    // Validate org slug matches current organization
    useEffect(() => {
        // Skip validation if no URL orgSlug (legacy routes or root)
        if (!urlOrgSlug) return;

        // Skip validation if no current organization yet (still loading)
        if (!currentOrganization) return;

        const expectedSlug = getOrgSlug(currentOrganization);

        // If URL orgSlug doesn't match current organization, redirect to correct URL
        if (urlOrgSlug !== expectedSlug && expectedSlug) {
            console.warn('⚠️ URL orgSlug mismatch, redirecting...', { urlOrgSlug, expectedSlug });

            // Build correct URL with current org slug
            let correctUrl: string;
            if (chatId && caseId) {
                correctUrl = buildChatUrl(caseId, chatId, expectedSlug);
            } else if (caseId) {
                correctUrl = buildCaseUrl(caseId, expectedSlug);
            } else {
                correctUrl = buildOrgHomeUrl(expectedSlug);
            }

            navigate(correctUrl, { replace: true });
        }
    }, [urlOrgSlug, currentOrganization, caseId, chatId, navigate]);

    // Sync URL -> Redux
    useEffect(() => {
        let hasChanged = false;

        // If URL has caseId but Redux doesn't match
        if (caseId && caseId !== currentCaseIdRef.current) {
            // Pass both caseId and chatId to the thunk so it can restore the full state
            dispatch(setNewCurrentCaseThunk({ caseId, initialChatId: chatId }));
            hasChanged = true;
        }

        // If URL has chatId but Redux doesn't match (and caseId matches)
        if (chatId && chatId !== currentChatIdRef.current && caseId === currentCaseIdRef.current) {
            dispatch(setCurrentChatId(chatId));
            hasChanged = true;
        }

        // If URL doesn't have chatId but Redux does (and we are in a case route), clear Redux
        // This handles back button from chat to case view
        if (!chatId && currentChatIdRef.current && caseId) {
            dispatch(setCurrentChatId(null));
            hasChanged = true;
        }

        // If URL doesn't have caseId but Redux does, clear both case and chat
        if (!caseId && currentCaseIdRef.current) {
            dispatch(setCurrentCaseId(''));
            dispatch(setCurrentChatId(null));
            hasChanged = true;
        }

        if (hasChanged) {
            isSyncingFromUrl.current = true;
        }

    }, [caseId, chatId, dispatch]);

    // Sync Redux -> URL
    useEffect(() => {
        // If we are currently syncing from URL, don't update URL back
        if (isSyncingFromUrl.current) {
            // Check if state has converged with URL
            const caseMatch = currentCaseId === caseId || (!currentCaseId && !caseId);
            const chatMatch = (currentChatId || undefined) === chatId; // Handle null vs undefined

            if (caseMatch && chatMatch) {
                isSyncingFromUrl.current = false;
            }
            return;
        }

        // Use urlOrgSlug as fallback if currentOrgSlug is not loaded yet
        // This allows case/chat URLs to work even before Redux org is loaded
        const effectiveOrgSlug = currentOrgSlug || urlOrgSlug || undefined;

        // Build the correct URL based on current Redux state
        let targetUrl: string;

        if (currentCaseId && currentChatId) {
            // Chat view: /orgSlug/caseId/chatId
            targetUrl = buildChatUrl(currentCaseId, currentChatId, effectiveOrgSlug);
        } else if (currentCaseId) {
            // Case view: /orgSlug/caseId
            targetUrl = buildCaseUrl(currentCaseId, effectiveOrgSlug);
        } else {
            // Org home: /orgSlug
            // CRITICAL: Only navigate to org home if we have a valid org slug
            // This prevents clearing the URL before Redux has loaded
            if (!effectiveOrgSlug) {
                console.log('⏳ UrlSync: No org slug available yet, skipping navigation');
                return;
            }
            targetUrl = buildOrgHomeUrl(effectiveOrgSlug);
        }

        // Only navigate if the path is different to avoid loops
        if (location.pathname !== targetUrl) {
            navigate(targetUrl, { replace: true });
        }
    }, [currentCaseId, currentChatId, navigate, location.pathname, caseId, chatId, currentOrgSlug, urlOrgSlug]);

    return null;
};

export default UrlSync;
